import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, Handshake, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_BASE_URL } from '../../services/apiClient';
import AffiliateReport, { formatDate, formatMoney } from '../affiliates/AffiliateReport';

// Admin side of the consultant affiliate program (backend api/v1/affiliates.py):
// invite/promote consultants, manage their yearly agreement terms, and audit
// their referrals/commissions. Payouts are handled outside the platform.

const toDateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : '');
const plusOneYear = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const emptyTerm = (start = today()) => ({
  term_start: start,
  term_end: plusOneYear(start),
  commission_pct: 10,
  customer_discount_pct: 10,
  customer_discount_months: 12,
});

const termPayload = (t) => ({
  term_start: `${t.term_start}T00:00:00Z`,
  term_end: `${t.term_end}T00:00:00Z`,
  commission_pct: Number(t.commission_pct),
  customer_discount_pct: Number(t.customer_discount_pct),
  customer_discount_months: Number(t.customer_discount_months),
});

const errorMessage = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(', ');
  return fallback;
};

const fieldClass = 'bg-slate-800 border-slate-700 text-white';
const Label = ({ children }) => <label className="text-xs text-slate-400 mb-1 block">{children}</label>;

const TermFields = ({ value, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
    <div>
      <Label>Inicio del acuerdo</Label>
      <Input type="date" value={value.term_start} onChange={(e) => onChange({ ...value, term_start: e.target.value })} className={fieldClass} />
    </div>
    <div>
      <Label>Fin del acuerdo</Label>
      <Input type="date" value={value.term_end} onChange={(e) => onChange({ ...value, term_end: e.target.value })} className={fieldClass} />
    </div>
    <div>
      <Label>Comisión consultor (%)</Label>
      <Input type="number" min={0} max={100} step="0.5" value={value.commission_pct} onChange={(e) => onChange({ ...value, commission_pct: e.target.value })} className={fieldClass} />
    </div>
    <div>
      <Label>Descuento usuario (%)</Label>
      <Input type="number" min={0} max={100} step="0.5" value={value.customer_discount_pct} onChange={(e) => onChange({ ...value, customer_discount_pct: e.target.value })} className={fieldClass} />
    </div>
    <div>
      <Label>Meses de descuento</Label>
      <Input type="number" min={1} max={36} value={value.customer_discount_months} onChange={(e) => onChange({ ...value, customer_discount_months: e.target.value })} className={fieldClass} />
    </div>
  </div>
);

function AffiliateDetail({ affiliateId, headers, onBack }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTerm, setNewTerm] = useState(null);
  const [editingTerm, setEditingTerm] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/affiliates/${affiliateId}`, { headers });
      setReport(res.data);
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo cargar el consultor'));
    } finally {
      setLoading(false);
    }
  }, [affiliateId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const saveNewTerm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/affiliates/${affiliateId}/terms`, termPayload(newTerm), { headers });
      toast.success('Acuerdo agregado');
      setNewTerm(null);
      fetchReport();
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo agregar el acuerdo'));
    } finally {
      setSaving(false);
    }
  };

  const saveEditedTerm = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/affiliates/${affiliateId}/terms/${editingTerm.id}`, termPayload(editingTerm), { headers });
      toast.success('Acuerdo actualizado');
      setEditingTerm(null);
      fetchReport();
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo actualizar el acuerdo'));
    } finally {
      setSaving(false);
    }
  };

  const deleteTerm = async (termId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/affiliates/${affiliateId}/terms/${termId}`, { headers });
      toast.success('Acuerdo eliminado');
      fetchReport();
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo eliminar el acuerdo'));
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/affiliates/${affiliateId}/commissions.csv`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comisiones_${report.affiliate.code}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No se pudo descargar el CSV');
    }
  };

  if (loading && !report) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
      </div>
    );
  }
  if (!report) return null;

  const lastTermEnd = report.terms[0]?.term_end;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={onBack} className="border-slate-700 text-slate-300">
          <ArrowLeft className="w-4 h-4 mr-1" /> Consultores
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadCsv} className="border-slate-700 text-slate-300">
            <Download className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading} className="border-slate-700 text-slate-300">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white">{report.affiliate.email}</h3>
        <p className="text-sm text-slate-400 mb-4">
          Código <span className="text-white font-mono">{report.affiliate.code}</span>
          {!report.affiliate.user_id && ' · Invitado: aún no se ha registrado'}
        </p>

        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-300">Acuerdos anuales</h4>
          {!newTerm && (
            <Button
              size="sm"
              onClick={() => setNewTerm(emptyTerm(lastTermEnd ? toDateInput(lastTermEnd) : today()))}
              className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Renovar / nuevo acuerdo
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Cada acuerdo solo genera comisión por usuarios registrados durante ese mismo acuerdo.
        </p>

        {newTerm && (
          <div className="mb-3 rounded-lg border border-blue-500/30 bg-slate-950/50 p-4 space-y-3">
            <TermFields value={newTerm} onChange={setNewTerm} />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveNewTerm} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar acuerdo
              </Button>
              <Button size="sm" variant="outline" onClick={() => setNewTerm(null)} className="border-slate-700 text-slate-300">Cancelar</Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {report.terms.map((t) => (
            <div key={t.id} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
              {editingTerm?.id === t.id ? (
                <div className="space-y-3">
                  <TermFields value={editingTerm} onChange={setEditingTerm} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEditedTerm} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingTerm(null)} className="border-slate-700 text-slate-300">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <div className="text-slate-300">
                    {formatDate(t.term_start)} – {formatDate(t.term_end)}
                    {report.current_term?.id === t.id && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">Vigente</span>
                    )}
                    <span className="ml-3 text-slate-500">
                      {t.commission_pct}% comisión · {t.customer_discount_pct}% descuento × {t.customer_discount_months} meses
                    </span>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingTerm({ ...t, term_start: toDateInput(t.term_start), term_end: toDateInput(t.term_end) })}
                      className="border-slate-700 text-slate-300 h-7"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteTerm(t.id)} className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-7">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Same view the consultant sees, forced to the dark palette to match the admin panel */}
      <div className="dark">
        <AffiliateReport report={report} />
      </div>
    </div>
  );
}

export default function AffiliatesPanel({ authToken }) {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', code: '', ...emptyTerm() });
  const [selectedId, setSelectedId] = useState(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/affiliates`, { headers });
      setAffiliates(res.data.affiliates || []);
    } catch (err) {
      toast.error('No se pudieron cargar los consultores');
    } finally {
      setLoading(false);
    }
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  const handleCreate = async () => {
    if (!form.email.trim() || !form.code.trim()) {
      toast.error('Email y código son obligatorios');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/admin/affiliates`,
        { email: form.email.trim(), code: form.code.trim(), ...termPayload(form) },
        { headers }
      );
      toast.success(res.data.message);
      setForm({ email: '', code: '', ...emptyTerm() });
      fetchAffiliates();
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo crear el consultor'));
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (a) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/admin/affiliates/${a.id}`,
        { status: a.status === 'active' ? 'disabled' : 'active' },
        { headers }
      );
      fetchAffiliates();
    } catch (err) {
      toast.error('No se pudo actualizar el consultor');
    }
  };

  if (selectedId) {
    return (
      <AffiliateDetail
        affiliateId={selectedId}
        headers={headers}
        onBack={() => { setSelectedId(null); fetchAffiliates(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
          <Plus className="w-5 h-5 text-blue-400" /> Nuevo consultor
        </h3>
        <p className="text-slate-500 text-sm mb-5">
          Si el email ya tiene cuenta se convierte en consultor al instante; si no, queda invitado y se activa cuando se registre.
          Los consultores no pueden usar Selltrix: solo ven su panel de afiliados.
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Email del consultor</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="consultor@correo.com" className={fieldClass} />
            </div>
            <div>
              <Label>Código del enlace (3–20 letras o números)</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                placeholder="JUANPEREZ"
                maxLength={20}
                className={`${fieldClass} font-mono`}
              />
            </div>
          </div>
          <TermFields value={form} onChange={(t) => setForm({ ...form, ...t })} />
          <Button onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
            {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crear consultor
          </Button>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Handshake className="w-5 h-5 text-blue-400" /> Consultores ({affiliates.length})
          </h3>
          <Button variant="outline" size="sm" onClick={fetchAffiliates} disabled={loading} className="border-slate-700 text-slate-300">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {loading && affiliates.length === 0 ? (
          <div className="flex justify-center py-16">
            <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
          </div>
        ) : affiliates.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">No hay consultores todavía.</p>
        ) : (
          <div className="space-y-2">
            {affiliates.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 bg-slate-950/50 border border-slate-800 rounded-lg p-4">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{a.email}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">{a.code}</span>
                    {!a.user_id && <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">Invitado</span>}
                    {a.status === 'active' ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">Activo</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-700/50 text-slate-400">Desactivado</span>
                    )}
                    <span className="text-xs text-slate-500">
                      {a.referrals} referidos · {formatMoney(a.commission_cents)} en comisiones ·{' '}
                      {a.current_term_end ? `acuerdo hasta ${formatDate(a.current_term_end)}` : 'sin acuerdo vigente'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => toggleStatus(a)} className="border-slate-700 text-slate-300 text-xs h-7">
                    {a.status === 'active' ? 'Desactivar' : 'Activar'}
                  </Button>
                  <Button size="sm" onClick={() => setSelectedId(a.id)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7">
                    Ver detalle
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
